import { History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
  Badge,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Loading } from '@vnetwork/ui/feedback';
import { useObjectVersions, type ObjectVersion } from '@/api/s3';
import { formatGiB } from '@/pages/services/oss/storage-format';
import { Mono } from '../../s3-parts';

function fmtDate(raw?: string | null): string {
  if (!raw) return '—';
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

/** Read-only version history for objects under a prefix. */
export function ObjectVersionsDialog({
  serviceId,
  bucket,
  prefix,
  onClose,
}: {
  serviceId: string;
  bucket: string;
  prefix: string;
  onClose: () => void;
}) {
  const { data = [], isLoading } = useObjectVersions(serviceId, bucket, prefix);

  const columns: ColumnDef<ObjectVersion, unknown>[] = [
    { id: 'key', header: 'Key', cell: ({ row }) => <Mono>{row.original.key}</Mono> },
    {
      id: 'version',
      header: 'Version',
      size: 220,
      cell: ({ row }) => (
        <span style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
          <Mono>{row.original.version_id || '—'}</Mono>
          {row.original.is_latest && (
            <Badge tone="success" appearance="subtle" size="sm">latest</Badge>
          )}
        </span>
      ),
    },
    { id: 'size', header: 'Size', size: 110, cell: ({ row }) => formatGiB(row.original.size) },
    {
      id: 'last_modified',
      header: 'Modified',
      size: 200,
      cell: ({ row }) => <Mono>{fmtDate(row.original.last_modified)}</Mono>,
    },
  ];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Object Versions</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Loading label="Loading versions…" size="sm" />
          ) : data.length === 0 ? (
            <EmptyState size="sm" icon={<History size={20} />} title="No versions yet" description="Versioning may be disabled for this bucket." />
          ) : (
            <DataTable columns={columns} data={data} getRowId={(r) => `${r.key}:${r.version_id ?? r.last_modified ?? ''}`} />
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary">Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
